import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Divider,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ActionButton from "./ActionButton";
import { fittedEllipseRadii } from "./fittedEllipse";
import DeleteButton from "./DeleteButton";
import LabeledSlider from "./LabeledSlider";
import { toggleSelectorSx } from "./toggleSelectorSx";
import BrushIcon from "@mui/icons-material/Brush";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import UndoIcon from "@mui/icons-material/Undo";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useReduxPrefState } from "../../app/hooks";

const STONE: [number, number, number] = [0, 200, 90]; // how the mask is tinted over the picture
const BINS = 720; // angular samples of the outline, as the pipeline reads it

interface Backdrop {
  key: string;
  label: string;
  src: string;
}

interface MaskEditorProps {
  size: number; // the mask is a size×size bitmap, edited at full resolution and saved back
  backdrops: Backdrop[]; // one → no chooser; several → a toggle to switch the picture behind the mask
  srcMask: string; // the stored mask to load and correct
  autopaint?: boolean; // offer a threshold that (re)builds the mask from the first backdrop's greyscale
  fitDisk?: boolean; // offer Disk: the ellipse that best follows the outline (a stone's)
  prefPrefix: string; // namespaces the remembered tool/brush/… settings so editors don't clash
  onSave: (mask: string) => void;
  onDelete: () => void;
  onCancel: () => void;
}

// Andrew's monotone chain. The full mask's hull equals the hull of each row's leftmost and
// rightmost stone pixel, so a scan hands this only ~2·size points to wrap.
function convexHull(points: number[][]) {
  if (points.length < 3) return points;
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: number[], a: number[], b: number[]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (source: number[][]) => {
    const chain: number[][] = [];
    for (const p of source) {
      while (chain.length >= 2 && cross(chain[chain.length - 2], chain[chain.length - 1], p) <= 0)
        chain.pop();
      chain.push(p);
    }
    chain.pop();
    return chain;
  };
  return [...half(pts), ...half([...pts].reverse())];
}

// Smooth a radius-per-angle outline without rounding off what is genuinely sharp. A median
// despikes it; then a gaussian whose width varies — the full `sigma` along a gentle arc, down
// to almost nothing where the outline turns hard — so a tip or corner keeps its point while long
// edges round fully. Corners are found as the curvature that stands out above its wide baseline.
function smoothRadii(r: number[], sigma: number) {
  const n = r.length;
  const at = (a: number[], i: number) => a[((i % n) + n) % n];
  const median5 = (a: number[], i: number) =>
    [at(a, i - 2), at(a, i - 1), a[i], at(a, i + 1), at(a, i + 2)].sort((x, y) => x - y)[2];

  const med = r.map((_, i) => median5(r, i));
  const light = med.map((_, i) => (at(med, i - 1) + 2 * med[i] + at(med, i + 1)) / 4);
  const curv = light.map((_, i) => Math.abs(at(light, i - 1) - 2 * light[i] + at(light, i + 1)));

  const bw = 20;
  const baseline = curv.map((_, i) => {
    let s = 0;
    for (let k = -bw; k <= bw; k++) s += at(curv, i + k);
    return s / (2 * bw + 1);
  });
  const excess = curv.map((c, i) => Math.max(0, c - baseline[i]));
  const p95 = [...excess].sort((x, y) => x - y)[Math.floor(0.95 * (n - 1))] + 1e-6;
  const corner = excess.map((e) => Math.min(1, e / p95));

  const narrow = Math.min(0.8, sigma);
  const width = corner.map((c) => sigma - (sigma - narrow) * c);
  const cap = Math.ceil(3 * sigma) + 1;

  return med.map((_, i) => {
    const s = Math.max(width[i], 1e-3);
    const half = Math.min(cap, Math.ceil(3 * s));
    let acc = 0;
    let wsum = 0;
    for (let k = -half; k <= half; k++) {
      const w = Math.exp(-(k * k) / (2 * s * s));
      acc += w * at(med, i + k);
      wsum += w;
    }
    return acc / wsum;
  });
}

// A mask over a picture: paint it in, tidy it with smooth / star / convexify, and correct it under
// zoom. The mask lives on a full-resolution offscreen canvas; the visible canvas fills the space,
// draws the mask over the chosen backdrop, and zooms/pans over it. The box and detour editors are
// this component with different sizes, backdrops and (for the box) the autopaint threshold.
const MaskEditor: React.FC<MaskEditorProps> = ({ size, backdrops, srcMask, autopaint, fitDisk, prefPrefix, onSave, onDelete, onCancel }) => {
  const viewRef = useRef<HTMLCanvasElement | null>(null);
  const areaRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<HTMLCanvasElement | null>(null);
  const redrawRef = useRef<() => void>(() => {});
  const drawingRef = useRef(false);
  const panStartRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const historyRef = useRef<ImageData[]>([]);
  const greyRef = useRef<Uint8ClampedArray | null>(null); // the first backdrop's pixels, for autopaint
  const thresholdDraggingRef = useRef(false);

  const [images, setImages] = useState<Record<string, HTMLImageElement>>({});
  const [polygon, setPolygon] = useState<number[][]>([]);
  const [canvasSize, setCanvasSize] = useState(600);

  const [backdrop, setBackdrop] = useReduxPrefState(`${prefPrefix}Backdrop`, backdrops[0].key);
  const [tool, setTool] = useReduxPrefState(`${prefPrefix}Tool`, "brush");
  const [add, setAdd] = useState(true);
  const [radius, setRadius] = useReduxPrefState(`${prefPrefix}Radius`, 24);
  const [opacity, setOpacity] = useReduxPrefState(`${prefPrefix}Opacity`, 0.5);
  const [smoothStrength, setSmoothStrength] = useReduxPrefState(`${prefPrefix}Smooth`, 8);
  const [threshold, setThreshold] = useReduxPrefState(`${prefPrefix}Threshold`, 127);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const scale = canvasSize / size; // view pixels per image pixel at zoom 1
  const backdropSrcs = backdrops.map((b) => b.src).join("|");

  // Size the square canvas to fill whatever space is left below the toolbar.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const measure = () => {
      const s = Math.floor(Math.min(el.clientWidth, el.clientHeight)) - 4;
      if (s > 0) setCanvasSize(s);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clampPan = (x: number, y: number, z: number) => {
    const span = size * (1 - 1 / z);
    return { x: Math.min(Math.max(x, 0), span), y: Math.min(Math.max(y, 0), span) };
  };

  const pushHistory = () => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return;
    historyRef.current.push(ctx.getImageData(0, 0, size, size));
    if (historyRef.current.length > 30) historyRef.current.shift();
  };

  const undo = () => {
    const ctx = maskRef.current?.getContext("2d");
    const snapshot = historyRef.current.pop();
    if (!ctx || !snapshot) return;
    ctx.putImageData(snapshot, 0, 0);
    setPolygon([]);
    redrawRef.current();
  };

  // Snap the mask back to a true binary shape: any pixel at least half covered is stone, the rest
  // is background. Canvas fills anti-alias their edges, so this runs after each edit to keep the
  // mask — on screen and when saved — strictly black and white.
  const binarize = () => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return;
    const pixels = ctx.getImageData(0, 0, size, size);
    const { data } = pixels;
    for (let i = 0; i < data.length; i += 4) {
      const on = data[i + 3] >= 128;
      data[i] = STONE[0];
      data[i + 1] = STONE[1];
      data[i + 2] = STONE[2];
      data[i + 3] = on ? 255 : 0;
    }
    ctx.putImageData(pixels, 0, 0);
  };

  // The mask canvas holds the stored mask: stone tinted, background transparent.
  useEffect(() => {
    const mask = document.createElement("canvas");
    mask.width = mask.height = size;
    maskRef.current = mask;
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.src = srcMask;
    image.onload = () => {
      const ctx = mask.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(image, 0, 0, size, size);
      const pixels = ctx.getImageData(0, 0, size, size);
      const { data } = pixels;
      for (let i = 0; i < data.length; i += 4) {
        const on = (data[i] + data[i + 1] + data[i + 2]) / 3 > 128;
        data[i] = STONE[0];
        data[i + 1] = STONE[1];
        data[i + 2] = STONE[2];
        data[i + 3] = on ? 255 : 0;
      }
      ctx.putImageData(pixels, 0, 0);
      redrawRef.current();
    };
  }, [srcMask, size]);

  // Load every backdrop; capture the first one's greyscale for the autopaint threshold.
  useEffect(() => {
    backdrops.forEach((bd, index) => {
      const image = new window.Image();
      image.crossOrigin = "anonymous";
      image.src = bd.src;
      image.onload = () => {
        setImages((prev) => ({ ...prev, [bd.key]: image }));
        if (autopaint && index === 0) {
          const temp = document.createElement("canvas");
          temp.width = temp.height = size;
          const ctx = temp.getContext("2d");
          if (!ctx) return;
          ctx.drawImage(image, 0, 0, size, size);
          greyRef.current = ctx.getImageData(0, 0, size, size).data;
        }
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backdropSrcs, size]);

  // Redraw the view: the chosen backdrop, the mask over it, and any outline being drawn. Everything
  // is drawn in full-resolution coordinates; one transform applies the zoom and pan, so k view
  // pixels cover one image pixel and (pan.x, pan.y) sits at the top-left corner.
  useEffect(() => {
    redrawRef.current = () => {
      const ctx = viewRef.current?.getContext("2d");
      const mask = maskRef.current;
      if (!ctx || !mask) return;
      const side = size * scale;
      const k = scale * zoom;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, side, side);
      ctx.setTransform(k, 0, 0, k, -pan.x * k, -pan.y * k);
      const picture = images[backdrop];
      if (picture) {
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(picture, 0, 0, size, size);
      }
      // Crisp pixels for the mask, so a zoomed-in edit shows exactly what is painted.
      ctx.imageSmoothingEnabled = false;
      ctx.globalAlpha = opacity;
      ctx.drawImage(mask, 0, 0, size, size);
      ctx.globalAlpha = 1;
      if (polygon.length) {
        ctx.strokeStyle = "yellow";
        ctx.lineWidth = 2 / k;
        ctx.beginPath();
        polygon.forEach(([x, y], i) => {
          const method = i === 0 ? "moveTo" : "lineTo";
          ctx[method](x, y);
        });
        ctx.stroke();
        polygon.forEach(([x, y]) => ctx.fillRect(x - 3 / k, y - 3 / k, 6 / k, 6 / k));
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    };
    redrawRef.current();
  }, [images, backdrop, opacity, polygon, scale, zoom, pan, size]);

  // Wheel zooms toward the cursor. A native non-passive listener, so it can stop the page scroll.
  useEffect(() => {
    const canvas = viewRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const z = Math.min(8, Math.max(1, zoom * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
      const kOld = scale * zoom;
      const kNew = scale * z;
      const fx = pan.x + e.offsetX / kOld;
      const fy = pan.y + e.offsetY / kOld;
      setZoom(z);
      setPan(clampPan(fx - e.offsetX / kNew, fy - e.offsetY / kNew, z));
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, pan, scale]);

  // Ctrl/Cmd+Z undoes the last edit. undo reads only refs, so binding once is enough.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stone = `rgba(${STONE[0]},${STONE[1]},${STONE[2]},1)`;

  // Snap the mask to strictly binary right after a brush dab, so what was painted shows at full
  // strength at once and a thin stroke is not lost to the half-cover rule that binarize() applies
  // only on mouse-up. Adding: any pixel the brush touched at all becomes solid; erasing: only a
  // fully-cleared pixel goes — so the same thin brush paints and rubs out what it crosses.
  const snapStroke = (adding: boolean) => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return;
    const pixels = ctx.getImageData(0, 0, size, size);
    const { data } = pixels;
    for (let i = 0; i < data.length; i += 4) {
      const on = adding ? data[i + 3] > 0 : data[i + 3] >= 255;
      data[i] = STONE[0];
      data[i + 1] = STONE[1];
      data[i + 2] = STONE[2];
      data[i + 3] = on ? 255 : 0;
    }
    ctx.putImageData(pixels, 0, 0);
  };

  // Paint at a full-resolution point; the brush keeps a constant on-screen size, so it reaches
  // finer detail the more the view is zoomed in.
  const paint = (fx: number, fy: number) => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.globalCompositeOperation = add ? "source-over" : "destination-out";
    ctx.fillStyle = stone;
    ctx.beginPath();
    ctx.arc(fx, fy, radius / (scale * zoom), 0, 2 * Math.PI);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    snapStroke(add);
    redrawRef.current();
  };

  const fillPolygon = () => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx || polygon.length < 3) return;
    pushHistory();
    ctx.globalCompositeOperation = add ? "source-over" : "destination-out";
    ctx.fillStyle = stone;
    ctx.beginPath();
    polygon.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    binarize();
    setPolygon([]);
  };

  // (Re)build the mask from the backdrop's greyscale: every pixel at or below the threshold is stone.
  const applyThreshold = (value: number) => {
    const grey = greyRef.current;
    const ctx = maskRef.current?.getContext("2d");
    if (!grey || !ctx) return;
    const out = ctx.createImageData(size, size);
    for (let i = 0; i < grey.length; i += 4) {
      const on = (grey[i] + grey[i + 1] + grey[i + 2]) / 3 <= value;
      out.data[i] = STONE[0];
      out.data[i + 1] = STONE[1];
      out.data[i + 2] = STONE[2];
      out.data[i + 3] = on ? 255 : 0;
    }
    ctx.putImageData(out, 0, 0);
    redrawRef.current();
  };

  // Fill the mask out to its convex hull — closing the dents a hand-drawn outline leaves. Built on
  // pixel CENTRES: the hull wraps the lit pixels' centres, and a pixel is lit when its own centre
  // falls inside or on that hull. This makes the operation idempotent (the result's centre-hull is
  // the same hull, so a second click changes nothing) and never-shrinking (every lit pixel's centre
  // is inside its own hull), instead of the canvas-fill rounding that shaved or grew the edge.
  const convexify = () => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return;
    const { data } = ctx.getImageData(0, 0, size, size);
    const centres: number[][] = [];
    for (let y = 0; y < size; y++) {
      let left = -1;
      let right = -1;
      for (let x = 0; x < size; x++) {
        if (data[(y * size + x) * 4 + 3] > 0) {
          if (left < 0) left = x;
          right = x;
        }
      }
      if (left >= 0) {
        centres.push([left + 0.5, y + 0.5]);
        centres.push([right + 0.5, y + 0.5]);
      }
    }
    const hull = convexHull(centres);
    if (hull.length < 3) return;

    // A point is inside or on a convex polygon when it stays on the same side of every edge; a zero
    // cross-product (on an edge) counts as inside, so the hull's own vertices are kept.
    const inHull = (px: number, py: number) => {
      let pos = false;
      let neg = false;
      for (let i = 0; i < hull.length; i++) {
        const a = hull[i];
        const b = hull[(i + 1) % hull.length];
        const c = (b[0] - a[0]) * (py - a[1]) - (b[1] - a[1]) * (px - a[0]);
        if (c > 0) pos = true;
        else if (c < 0) neg = true;
        if (pos && neg) return false;
      }
      return true;
    };

    pushHistory();
    const out = ctx.getImageData(0, 0, size, size);
    const od = out.data;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (inHull(x + 0.5, y + 0.5)) {
          const i = (y * size + x) * 4;
          od[i] = STONE[0];
          od[i + 1] = STONE[1];
          od[i + 2] = STONE[2];
          od[i + 3] = 255;
        }
      }
    }
    ctx.putImageData(out, 0, 0);
    redrawRef.current();
  };

  // Read the mask as a radius-per-angle outline: the centroid, and the outer end of the contiguous
  // white run along each ray (so detached specks are ignored — the boundary the pipeline reads).
  const maskBoundary = () => {
    const ctx = maskRef.current?.getContext("2d");
    if (!ctx) return null;
    const { data } = ctx.getImageData(0, 0, size, size);
    const lit = (x: number, y: number) =>
      x >= 0 && x < size && y >= 0 && y < size && data[(y * size + x) * 4 + 3] > 0;
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (data[(y * size + x) * 4 + 3] > 0) {
          sx += x;
          sy += y;
          n++;
        }
      }
    }
    if (n === 0) return null;
    const cx = sx / n;
    const cy = sy / n;
    const r = new Array<number>(BINS);
    for (let i = 0; i < BINS; i++) {
      const angle = (i / BINS) * 2 * Math.PI;
      const dx = Math.cos(angle);
      const dy = Math.sin(angle);
      let rr = 0;
      for (let t = 1; lit(Math.round(cx + dx * t), Math.round(cy + dy * t)); t++) rr = t;
      r[i] = rr;
    }
    return { ctx, cx, cy, r };
  };

  const fillRadii = (ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number[]) => {
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = stone;
    ctx.beginPath();
    for (let i = 0; i < BINS; i++) {
      const angle = (i / BINS) * 2 * Math.PI;
      const x = cx + r[i] * Math.cos(angle);
      const y = cy + r[i] * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    binarize();
    redrawRef.current();
  };

  // Make the mask the ellipse that best follows its outline — robustly, so a bump or a dent does not
  // drag it — whatever the shape: for an outline drawn almost round, the disk it was meant to be.
  const fitDiskShape = () => {
    const b = maskBoundary();
    if (!b) return;
    pushHistory();
    fillRadii(b.ctx, b.cx, b.cy, fittedEllipseRadii(b.cx, b.cy, b.r));
  };

  const starShape = () => {
    const b = maskBoundary();
    if (!b) return;
    pushHistory();
    fillRadii(b.ctx, b.cx, b.cy, b.r);
  };

  const smooth = () => {
    const b = maskBoundary();
    if (!b) return;
    pushHistory();
    fillRadii(b.ctx, b.cx, b.cy, smoothRadii(b.r, smoothStrength));
  };

  const clear = () => {
    pushHistory();
    maskRef.current?.getContext("2d")?.clearRect(0, 0, size, size);
    setPolygon([]);
    redrawRef.current();
  };

  // Export the mask as the white-on-black bitmap the trainer reads.
  const save = () => {
    const mask = maskRef.current;
    if (!mask) return;
    const out = document.createElement("canvas");
    out.width = out.height = size;
    const ctx = out.getContext("2d");
    const from = mask.getContext("2d");
    if (!ctx || !from) return;
    const source = from.getImageData(0, 0, size, size).data;
    const image = ctx.createImageData(size, size);
    for (let i = 0; i < source.length; i += 4) {
      const on = source[i + 3] > 0 ? 255 : 0;
      image.data[i] = image.data[i + 1] = image.data[i + 2] = on;
      image.data[i + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);
    onSave(out.toDataURL("image/png"));
  };

  const onPointer = (e: React.MouseEvent, down: boolean) => {
    const { offsetX, offsetY } = e.nativeEvent;
    const k = scale * zoom;
    if (tool === "pan") {
      if (down) panStartRef.current = { mx: offsetX, my: offsetY, px: pan.x, py: pan.y };
      else if (panStartRef.current) {
        const s = panStartRef.current;
        setPan(clampPan(s.px - (offsetX - s.mx) / k, s.py - (offsetY - s.my) / k, zoom));
      }
      return;
    }
    const fx = pan.x + offsetX / k;
    const fy = pan.y + offsetY / k;
    if (tool === "polygon") {
      if (down) setPolygon((points) => [...points, [fx, fy]]);
      return;
    }
    if (down) {
      pushHistory();
      drawingRef.current = true;
    }
    if (drawingRef.current) paint(fx, fy);
  };

  const endStroke = () => {
    if (drawingRef.current) {
      binarize();
      redrawRef.current();
    }
    drawingRef.current = false;
    panStartRef.current = null;
  };

  // Each parameter sits with the control it affects: overlay with the backdrop, brush size with the
  // brush tool, the smoothing amount with the Smooth button.
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", gap: 1, p: 1 }}>
      <Paper variant="outlined" sx={{ px: 1, py: 0.75 }}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1.5} rowGap={1}>
          {/* Backdrop (only a toggle when there is a choice) + how strongly the mask overlays it */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {backdrops.length > 1 && (
              <ToggleButtonGroup size="small" sx={toggleSelectorSx} value={backdrop} exclusive onChange={(_, v) => v && setBackdrop(v)}>
                {backdrops.map((bd) => (
                  <ToggleButton key={bd.key} value={bd.key}>
                    {bd.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            <LabeledSlider label="Overlay" width={104} min={0} max={1} step={0.05} value={opacity} onChange={(_, v) => setOpacity(v as number)} />
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Tool + how it paints + the brush size */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <ToggleButtonGroup size="small" sx={toggleSelectorSx} value={tool} exclusive onChange={(_, v) => v && setTool(v)}>
              <ToggleButton value="brush" title="Brush">
                <BrushIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="polygon" title="Polygon">
                <PentagonOutlinedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="pan" title="Pan">
                <OpenWithIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" sx={toggleSelectorSx} value={add} exclusive onChange={(_, v) => v !== null && setAdd(v)}>
              <ToggleButton value={true}>Add</ToggleButton>
              <ToggleButton value={false}>Erase</ToggleButton>
            </ToggleButtonGroup>
            <LabeledSlider label="Brush size" width={104} min={2} max={120} value={radius} onChange={(_, v) => setRadius(v as number)} />
          </Stack>

          <Divider orientation="vertical" flexItem />

          {/* Shape ops; Smooth carries its own strength; the box editor adds a threshold autopaint */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <ActionButton size="small" disabled={polygon.length < 3} onClick={fillPolygon}>Fill</ActionButton>
            <ActionButton size="small" onClick={starShape}>Star</ActionButton>
            <ActionButton size="small" onClick={convexify}>Convex</ActionButton>
            {fitDisk && <ActionButton size="small" onClick={fitDiskShape}>Disk</ActionButton>}
            <ActionButton size="small" onClick={smooth}>Smooth</ActionButton>
            <LabeledSlider label="Strength" width={104} min={1} max={40} value={smoothStrength} onChange={(_, v) => setSmoothStrength(v as number)} />
            {autopaint && (
              <LabeledSlider
                label="Threshold"
                width={104}
                min={1}
                max={254}
                value={threshold}
                onChange={(_, v) => {
                  if (!thresholdDraggingRef.current) {
                    pushHistory();
                    thresholdDraggingRef.current = true;
                  }
                  setThreshold(v as number);
                  applyThreshold(v as number);
                }}
                onChangeCommitted={() => {
                  thresholdDraggingRef.current = false;
                }}
              />
            )}
          </Stack>

          <Divider orientation="vertical" flexItem />

          <ActionButton size="small" startIcon={<UndoIcon />} onClick={undo}>Undo</ActionButton>
          <DeleteButton size="small" onClick={clear}>Clear</DeleteButton>

          <Box sx={{ flexGrow: 1 }} />

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" alignItems="center" spacing={1}>
            <ActionButton size="small" startIcon={<SaveIcon />} onClick={save}>Save</ActionButton>
            <Button size="small" variant="outlined" onClick={onCancel}>Cancel</Button>
            <DeleteButton size="small" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>Delete</DeleteButton>
          </Stack>
        </Stack>
      </Paper>

      <Box
        ref={areaRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          borderRadius: 1,
          cursor: tool === "pan" ? "grab" : "crosshair",
        }}
      >
        <canvas
          ref={viewRef}
          width={canvasSize}
          height={canvasSize}
          style={{ display: "block", touchAction: "none", boxShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
          onMouseDown={(e) => {
            e.preventDefault();
            onPointer(e, true);
          }}
          onMouseMove={(e) => onPointer(e, false)}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
        />
      </Box>
    </Box>
  );
};

export default MaskEditor;
